import React from "react";
import { useGetBiSessionEventsQuery } from "../../app/api/console/consoleBiApiSlice";
import { describeEvent, eventOffsetLabel, isActionEvent } from "./sessionEventLabels";

/**
 * Raw event timeline for one Hub session (expanded row in the sessions table).
 * Fetched lazily through the Console platform-proxy; only `hub_v2` sessions have
 * events (V1 pseudo-sessions are built from `user_actions`).
 */
export default function SessionEventsPanel({ session, useProd, hubUrl }) {
  const { data, isLoading, error } = useGetBiSessionEventsQuery(
    { sessionId: session.session_id, useProd },
    { skip: session.source !== "hub_v2" }
  );

  if (session.source !== "hub_v2") {
    return (
      <p className='text-body-xs text-text-tertiary px-4 py-3'>
        {session.source === "login"
          ? "Sign-in with no Hub telemetry in reach — nothing else to show for this session."
          : "Legacy Hub V1 activity (sessionized from user_actions) — no event-level timeline."}
      </p>
    );
  }
  if (isLoading) {
    return (
      <div className='px-4 py-3 space-y-2'>
        {[...Array(4)].map((_, i) => (
          <div key={i} className='h-4 bg-surface-neutral rounded animate-pulse w-2/3' />
        ))}
      </div>
    );
  }
  if (error) {
    return <p className='text-body-xs text-severity-high px-4 py-3'>Failed to load session events</p>;
  }
  // `/detail` returns { session_id, events, entities }; tolerate the older bare list too.
  const events = Array.isArray(data) ? data : Array.isArray(data?.events) ? data.events : [];
  const entities = (!Array.isArray(data) && data?.entities) || {};
  // Prefer the Hub origin Platform reported for this env; fall back to the list's.
  const hubBase = (!Array.isArray(data) && data?.hub_url) || hubUrl || null;
  if (!events.length) {
    return <p className='text-body-xs text-text-tertiary px-4 py-3'>No events stored for this session</p>;
  }
  const startIso = events[0]?.timestamp || session.started_at;

  return (
    <div className='px-4 py-3'>
      <div className='flex items-center justify-between mb-2'>
        <p className='text-body-xs font-semibold text-text-secondary'>
          {events.length} events · session <span className='font-mono'>{session.session_id.slice(0, 8)}</span>
        </p>
      </div>
      <ol className='space-y-1'>
        {events.map((ev) => {
          const { label, detail, href, id } = describeEvent(ev, entities, hubBase);
          const action = isActionEvent(ev.event_type);
          const rawId = id || ev.finding_id || ev.group_id || ev.scan_id || null;
          return (
            <li key={ev.id} className='flex items-baseline gap-3 text-body-xs'>
              <span className='w-14 shrink-0 font-mono text-text-tertiary text-right'>
                {eventOffsetLabel(startIso, ev.timestamp)}
              </span>
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded-md font-medium ${
                  action ? "bg-severity-medium/10 text-severity-medium" : "bg-surface-neutral text-text-secondary"
                }`}>
                {label}
              </span>
              {detail ? (
                href ? (
                  <a
                    href={href}
                    target='_blank'
                    rel='noopener noreferrer'
                    title={`${detail} — open on Hub`}
                    className='text-accent-purple truncate underline decoration-dotted underline-offset-2 hover:decoration-solid'>
                    {detail} ↗
                  </a>
                ) : (
                  <span className='text-text-secondary truncate' title={detail}>{detail}</span>
                )
              ) : null}
              {rawId ? (
                <span className='font-mono text-[10px] text-text-tertiary shrink-0 select-all' title='Identifier'>
                  {rawId}
                </span>
              ) : null}
              <span className='ml-auto font-mono text-[10px] text-text-tertiary shrink-0'>{ev.event_type}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
